import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { balanceService } from '../../services/balanceService';
import LoginModal from '../Auth/LoginModal';
import RegisterModal from '../Auth/RegisterModal';
import ForgotPasswordModal from '../Auth/ForgotPasswordModal';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import { buildNav, navLanguage } from '../../data/navMenu';
import { SITE_LANGUAGE_LANDINGS } from '../../data/siteLanguages';
import './Header.css';

const LANGUAGES = [
  { code: 'en', path: '/', nativeLabel: 'English' },
  ...SITE_LANGUAGE_LANDINGS.map((language) => ({
    code: language.code,
    path: language.path,
    nativeLabel: language.nativeLabel,
  })),
];

const HOME_PATHS = new Set(LANGUAGES.map((language) => language.path));

function isHome(pathname) {
  const path = String(pathname || '/');
  if (HOME_PATHS.has(path)) return true;
  return HOME_PATHS.has(path.endsWith('/') ? path : `${path}/`);
}

function pathOf(to) {
  return String(to || '').split('?')[0];
}

function isItemCurrent(to, pathname) {
  return pathOf(to) === pathname;
}

function sectionHas(groups, pathname) {
  return groups.some((group) => group.links.some((link) => {
    const path = pathOf(link.to);
    return pathname === path || (path !== '/' && pathname.startsWith(path));
  }));
}

function Dropdown({ name, label, active, openMenu, toggleMenu, align, children }) {
  return (
    <div className={`navbar-dropdown ${openMenu === name ? 'open' : ''}`}>
      <button
        type="button"
        className={`navbar-link navbar-dropdown-toggle ${active ? 'active' : ''}`}
        aria-haspopup="true"
        aria-expanded={openMenu === name}
        onClick={() => toggleMenu(name)}
      >
        {label} <span className="navbar-caret" aria-hidden="true">▾</span>
      </button>
      <div className={`navbar-dropdown-menu ${align || ''}`} role="group" aria-label={label}>
        {children}
      </div>
    </div>
  );
}

function Group({ label, links }) {
  return (
    <div className="navbar-dropdown-group">
      <p className="navbar-dropdown-label">{label}</p>
      {links.map((link) => (
        <NavLink key={link.id} to={link.to} className="navbar-dropdown-item">
          {link.label}
        </NavLink>
      ))}
    </div>
  );
}

const Header = React.forwardRef((props, ref) => {
  const siteTitle = 'NowTarot AI';
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [balances, setBalances] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const { user } = useAuth();
  const location = useLocation();
  const nav = buildNav(navLanguage(location.pathname));
  const { copy } = nav;

  useEffect(() => {
    setOpenMenu(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    const node = menuRef.current?.querySelector('a, button, summary');
    node?.focus();
    return undefined;
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!openMenu && !mobileMenuOpen) return undefined;
    const handleKey = (event) => {
      if (event.key !== 'Escape') return;
      setOpenMenu(null);
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    const handleClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      const onToggle = toggleRef.current && toggleRef.current.contains(event.target);
      if (mobileMenuOpen && !inMenu && !onToggle) setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [openMenu, mobileMenuOpen]);

  const isPrivateRoute = () => {
    const privateRoutes = ['/account', '/support', '/wallet', '/home', '/llm', '/translate'];
    return privateRoutes.includes(location.pathname);
  };

  const fetchBalances = async () => {
    try {
      const data = await balanceService.getBalances();
      setBalances(data);
    } catch (err) {
      console.error('Failed to fetch balances:', err);
    }
  };

  useEffect(() => {
    if (user) fetchBalances();
  }, [user]);

  useEffect(() => {
    if (ref) ref.current = { refreshBalance: fetchBalances };
  }, [ref]);

  const showSiteNav = !isPrivateRoute();
  const toggleMenu = (name) => setOpenMenu((current) => (current === name ? null : name));
  const toggleMobileMenu = () => {
    setMobileMenuOpen((open) => {
      if (!open) {
        fetch('/api/v1/visitors/mobile-menu', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ page: window.location.pathname }),
          keepalive: true,
        }).catch(() => {});
      }
      return !open;
    });
  };
  const tarotActive = sectionHas(nav.tarot, location.pathname);
  const astroActive = sectionHas(nav.astrology, location.pathname);
  const popularActive = nav.popular.some((link) => {
    const path = pathOf(link.to);
    return location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
  });

  return (
    <>
      <nav className={`top-navbar ${user && !showSiteNav ? 'compact' : ''}`} aria-label="Main" lang={nav.lang} dir={nav.dir}>
        <div className="navbar-content">
          <div className="navbar-logo">
            <Link className="logo-button navbar-title" to={nav.home} aria-label={siteTitle}>
              <span className="logo-mark" aria-hidden="true" />
              <span className="logo-word" aria-hidden="true">Now<span className="logo-word-accent">Tarot</span></span>
              <span className="logo-ai" aria-hidden="true">AI</span>
            </Link>
          </div>
          {showSiteNav && (
            <div className="navbar-links" ref={dropdownRef}>
              <Dropdown
                name="popular"
                label={copy.popular}
                active={popularActive}
                openMenu={openMenu}
                toggleMenu={toggleMenu}
              >
                {nav.popular.map((link) => (
                  <NavLink key={link.id} to={link.to} className="navbar-dropdown-item">
                    {link.label}
                  </NavLink>
                ))}
              </Dropdown>
              <Dropdown
                name="tarot"
                label={copy.tarot}
                active={tarotActive}
                openMenu={openMenu}
                toggleMenu={toggleMenu}
              >
                {nav.tarot.map((group) => (
                  <Group key={group.id} label={group.label} links={group.links} />
                ))}
              </Dropdown>
              <Dropdown
                name="astrology"
                label={copy.astrology}
                active={astroActive}
                openMenu={openMenu}
                toggleMenu={toggleMenu}
                align="align-end"
              >
                {nav.astrology.map((group) => (
                  <Group key={group.id} label={group.label} links={group.links} />
                ))}
              </Dropdown>
              <NavLink to={nav.home} end className="navbar-ask">
                {copy.ask}
              </NavLink>
            </div>
          )}
          <div className="navbar-buttons">
            {showSiteNav && !isHome(location.pathname) && (
              <NavLink to={nav.home} end className="navbar-shortcut navbar-mobile-ask-shortcut">
                {copy.askShort}
              </NavLink>
            )}
            <ThemeToggle />
            {user ? (
              <>
                {(isPrivateRoute() && balances) && (
                  <div className="balance-display">
                    <div className="balance-item">
                      <span className="balance-label">Wallet:</span>
                      <span className="balance-value">${Number(balances.wallet_balance).toFixed(2)}</span>
                    </div>
                    {user.calling_enabled && (
                      <div className="balance-item">
                        <span className="balance-label">Free Minutes:</span>
                        <span className="balance-value">{balances.calling_minutes_balance}</span>
                      </div>
                    )}
                  </div>
                )}
                <Link className="nav-button" to="/account">Settings</Link>
                <Link className="nav-button" to="/support">Help</Link>
              </>
            ) : (
              <>
                <button type="button" className="nav-button nav-button-login" onClick={() => setShowLogin(true)}>
                  Login
                </button>
                <button type="button" className="nav-button primary" onClick={() => setShowRegister(true)}>
                  Sign Up
                </button>
              </>
            )}
            {showSiteNav && (
              <button
                type="button"
                className="navbar-menu-toggle"
                ref={toggleRef}
                aria-label={mobileMenuOpen ? copy.closeMenu : copy.openMenu}
                aria-expanded={mobileMenuOpen}
                aria-controls="navbar-mobile-menu"
                onClick={toggleMobileMenu}
              >
                <span className="navbar-menu-icon" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </nav>

      {showSiteNav && mobileMenuOpen && (
        <div className="navbar-mobile-menu" id="navbar-mobile-menu" ref={menuRef} lang={nav.lang} dir={nav.dir}>
          <p className="navbar-mobile-heading">{copy.popular}</p>
          {nav.popular.map((link) => (
            <NavLink
              key={link.id}
              to={link.to}
              className="navbar-mobile-link"
              aria-current={isItemCurrent(link.to, location.pathname) ? 'page' : undefined}
            >
              {link.label}
            </NavLink>
          ))}

          <details className="navbar-mobile-section">
            <summary>{copy.tarot}</summary>
            {nav.tarot.map((group) => (
              <div key={group.id} className="navbar-mobile-group">
                <p className="navbar-mobile-heading">{group.label}</p>
                {group.links.map((link) => (
                  <NavLink key={link.id} to={link.to} className="navbar-mobile-link">{link.label}</NavLink>
                ))}
              </div>
            ))}
          </details>

          <details className="navbar-mobile-section">
            <summary>{copy.astrology}</summary>
            {nav.astrology.map((group) => (
              <div key={group.id} className="navbar-mobile-group">
                <p className="navbar-mobile-heading">{group.label}</p>
                {group.links.map((link) => (
                  <NavLink key={link.id} to={link.to} className="navbar-mobile-link">{link.label}</NavLink>
                ))}
              </div>
            ))}
          </details>

          <NavLink to={nav.home} end className="navbar-mobile-ask">{copy.ask}</NavLink>

          <div className="navbar-mobile-settings">
            <details className="navbar-mobile-section">
              <summary>{copy.language}</summary>
              <div className="navbar-lang-list">
                {LANGUAGES.map((language) => (
                  <Link
                    key={language.code}
                    to={language.path}
                    hrefLang={language.code}
                    lang={language.code}
                    aria-current={language.code === nav.lang ? 'page' : undefined}
                  >
                    {language.nativeLabel}
                  </Link>
                ))}
              </div>
            </details>
            <ThemeToggle />
          </div>

          {user ? (
            <>
              <NavLink to="/account" className="navbar-mobile-link navbar-mobile-login">Settings</NavLink>
              <NavLink to="/support" className="navbar-mobile-link navbar-mobile-login">Help</NavLink>
            </>
          ) : (
            <>
              <button
                type="button"
                className="navbar-mobile-link navbar-mobile-login"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setShowLogin(true);
                }}
              >
                Login
              </button>
              <button
                type="button"
                className="navbar-mobile-link navbar-mobile-login"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setShowRegister(true);
                }}
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      )}

      {showLogin && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSwitchToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
          onSwitchToForgotPassword={() => {
            setShowLogin(false);
            setShowForgotPassword(true);
          }}
        />
      )}

      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onSwitchToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}

      {showForgotPassword && (
        <ForgotPasswordModal
          onClose={() => setShowForgotPassword(false)}
          onSwitchToLogin={() => {
            setShowForgotPassword(false);
            setShowLogin(true);
          }}
        />
      )}
    </>
  );
});

export default Header;
